import Papa from 'papaparse';
import type {
  POSSourceParser,
  POSImportData,
  POSParsedProduct,
  POSParsedVariant,
  POSParsedCompositeComponent,
  POSOutletInventory,
  POSParseWarning,
  OutletLocationMapping,
} from './types';

// Column indices are determined dynamically from the header row.
// These are the known Lightspeed CSV column names.
const LIGHTSPEED_COLUMNS = {
  id: 'id',
  handle: 'handle',
  sku: 'sku',
  composite_name: 'composite_name',
  composite_sku: 'composite_sku',
  composite_quantity: 'composite_quantity',
  name: 'name',
  description: 'description',
  product_category: 'product_category',
  variant_option_one_name: 'variant_option_one_name',
  variant_option_one_value: 'variant_option_one_value',
  variant_option_two_name: 'variant_option_two_name',
  variant_option_two_value: 'variant_option_two_value',
  variant_option_three_name: 'variant_option_three_name',
  variant_option_three_value: 'variant_option_three_value',
  tags: 'tags',
  supply_price: 'supply_price',
  retail_price: 'retail_price',
  account_code: 'account_code',
  account_code_purchase: 'account_code_purchase',
  brand_name: 'brand_name',
  supplier_name: 'supplier_name',
  supplier_code: 'supplier_code',
  active: 'active',
  track_inventory: 'track_inventory',
} as const;

/** A single raw row parsed from the Lightspeed CSV */
interface LightspeedRow {
  rowNumber: number;
  id: string;
  handle: string;
  sku: string;
  composite_name: string;
  composite_sku: string;
  composite_quantity: string;
  name: string;
  description: string;
  product_category: string;
  variant_option_one_name: string;
  variant_option_one_value: string;
  variant_option_two_name: string;
  variant_option_two_value: string;
  variant_option_three_name: string;
  variant_option_three_value: string;
  tags: string;
  supply_price: string;
  retail_price: string;
  account_code: string;
  account_code_purchase: string;
  brand_name: string;
  supplier_name: string;
  supplier_code: string;
  active: string;
  track_inventory: string;
  /** Dynamic outlet columns: key = full column header, value = cell value */
  outletColumns: Record<string, string>;
}

/** Outlet info extracted from column headers */
interface OutletColumnInfo {
  outletName: string;
  /** Column type: tax, inventory, reorder_point, restock_level */
  columnType: 'tax' | 'inventory' | 'reorder_point' | 'restock_level';
  /** Index into the raw CSV row */
  columnIndex: number;
}

// Regex patterns for detecting outlet column headers
const OUTLET_PATTERNS: Array<{ prefix: string; type: OutletColumnInfo['columnType'] }> = [
  { prefix: 'outlet_tax_', type: 'tax' },
  { prefix: 'inventory_', type: 'inventory' },
  { prefix: 'reorder_point_', type: 'reorder_point' },
  { prefix: 'restock_level_', type: 'restock_level' },
];

/**
 * Extract outlet names and column info from CSV headers.
 * Lightspeed encodes outlet names in column headers like:
 *   outlet_tax_KFTL, inventory_KFTL, reorder_point_KFTL, restock_level_KFTL
 */
function parseOutletColumns(headers: string[]): {
  outlets: string[];
  outletColumns: OutletColumnInfo[];
} {
  const outletSet = new Set<string>();
  const outletColumns: OutletColumnInfo[] = [];

  headers.forEach((header, index) => {
    for (const pattern of OUTLET_PATTERNS) {
      if (header.startsWith(pattern.prefix)) {
        const outletName = header.slice(pattern.prefix.length);
        if (outletName) {
          outletSet.add(outletName);
          outletColumns.push({
            outletName,
            columnType: pattern.type,
            columnIndex: index,
          });
        }
        break;
      }
    }
  });

  return {
    outlets: Array.from(outletSet),
    outletColumns,
  };
}

/**
 * Build a header-to-index map for the known Lightspeed columns.
 */
function buildColumnIndexMap(headers: string[]): Map<string, number> {
  const map = new Map<string, number>();
  headers.forEach((header, index) => {
    map.set(header.trim(), index);
  });
  return map;
}

/**
 * Parse a single CSV row into a typed LightspeedRow using the column index map.
 */
function parseRow(
  rawRow: string[],
  rowNumber: number,
  columnMap: Map<string, number>,
  outletColumns: OutletColumnInfo[]
): LightspeedRow {
  const get = (colName: string): string => {
    const idx = columnMap.get(colName);
    if (idx === undefined) return '';
    return (rawRow[idx] ?? '').trim();
  };

  // Collect outlet-specific columns
  const outletData: Record<string, string> = {};
  for (const oc of outletColumns) {
    const val = (rawRow[oc.columnIndex] ?? '').trim();
    const key = `${oc.columnType}__${oc.outletName}`;
    outletData[key] = val;
  }

  return {
    rowNumber,
    id: get(LIGHTSPEED_COLUMNS.id),
    handle: get(LIGHTSPEED_COLUMNS.handle),
    sku: get(LIGHTSPEED_COLUMNS.sku),
    composite_name: get(LIGHTSPEED_COLUMNS.composite_name),
    composite_sku: get(LIGHTSPEED_COLUMNS.composite_sku),
    composite_quantity: get(LIGHTSPEED_COLUMNS.composite_quantity),
    name: get(LIGHTSPEED_COLUMNS.name),
    description: get(LIGHTSPEED_COLUMNS.description),
    product_category: get(LIGHTSPEED_COLUMNS.product_category),
    variant_option_one_name: get(LIGHTSPEED_COLUMNS.variant_option_one_name),
    variant_option_one_value: get(LIGHTSPEED_COLUMNS.variant_option_one_value),
    variant_option_two_name: get(LIGHTSPEED_COLUMNS.variant_option_two_name),
    variant_option_two_value: get(LIGHTSPEED_COLUMNS.variant_option_two_value),
    variant_option_three_name: get(LIGHTSPEED_COLUMNS.variant_option_three_name),
    variant_option_three_value: get(LIGHTSPEED_COLUMNS.variant_option_three_value),
    tags: get(LIGHTSPEED_COLUMNS.tags),
    supply_price: get(LIGHTSPEED_COLUMNS.supply_price),
    retail_price: get(LIGHTSPEED_COLUMNS.retail_price),
    account_code: get(LIGHTSPEED_COLUMNS.account_code),
    account_code_purchase: get(LIGHTSPEED_COLUMNS.account_code_purchase),
    brand_name: get(LIGHTSPEED_COLUMNS.brand_name),
    supplier_name: get(LIGHTSPEED_COLUMNS.supplier_name),
    supplier_code: get(LIGHTSPEED_COLUMNS.supplier_code),
    active: get(LIGHTSPEED_COLUMNS.active),
    track_inventory: get(LIGHTSPEED_COLUMNS.track_inventory),
    outletColumns: outletData,
  };
}

/** Parse a numeric string, returning undefined if empty/NaN */
function toNumber(val: string): number | undefined {
  if (!val) return undefined;
  const num = Number(val);
  return isNaN(num) ? undefined : num;
}

/**
 * Check if a row is a composite component row (sub-row of a parent product).
 * In Lightspeed exports, composite component rows have the same `id` as the parent
 * but have `composite_name` and `composite_sku` populated while other product fields
 * (handle, retail_price, etc.) are empty.
 */
function isCompositeComponentRow(row: LightspeedRow): boolean {
  return !!row.composite_name && !row.handle;
}

/**
 * Check if a row represents a variant (has variant option values populated).
 */
function hasVariantOptions(row: LightspeedRow): boolean {
  return !!(
    row.variant_option_one_value ||
    row.variant_option_two_value ||
    row.variant_option_three_value
  );
}

/**
 * Extract variant attributes from a row as a name->value record.
 */
function extractVariantAttributes(row: LightspeedRow): Record<string, string> {
  const attrs: Record<string, string> = {};
  if (row.variant_option_one_name && row.variant_option_one_value) {
    attrs[row.variant_option_one_name] = row.variant_option_one_value;
  }
  if (row.variant_option_two_name && row.variant_option_two_value) {
    attrs[row.variant_option_two_name] = row.variant_option_two_value;
  }
  if (row.variant_option_three_name && row.variant_option_three_value) {
    attrs[row.variant_option_three_name] = row.variant_option_three_value;
  }
  return attrs;
}

/**
 * Extract per-outlet inventory data from a row's outlet columns.
 */
function extractOutletInventory(
  row: LightspeedRow,
  outlets: string[]
): Record<string, POSOutletInventory> {
  const result: Record<string, POSOutletInventory> = {};
  for (const outlet of outlets) {
    const inv = toNumber(row.outletColumns[`inventory__${outlet}`]);
    const reorderPt = toNumber(row.outletColumns[`reorder_point__${outlet}`]);
    const restockLvl = toNumber(row.outletColumns[`restock_level__${outlet}`]);

    // Only include outlets that have at least one data point
    if (inv !== undefined || reorderPt !== undefined || restockLvl !== undefined) {
      result[outlet] = {
        quantity: inv ?? 0,
        reorder_point: reorderPt,
        restock_level: restockLvl,
      };
    }
  }
  return result;
}

/**
 * Build a variant name from its attribute values.
 * e.g. { Size: "Large" } => "Large"
 * e.g. { Size: "Large", Color: "Red" } => "Large / Red"
 */
function buildVariantName(attributes: Record<string, string>): string {
  return Object.values(attributes).join(' / ');
}

/**
 * Group parsed rows into product families.
 *
 * Lightspeed CSV row grouping rules:
 * 1. Rows with the same `id` belong to the same product (composites share parent id)
 * 2. Rows with the same `handle` but different `id` are variants of the same product
 * 3. Composite component rows have `composite_name` populated but no `handle`
 */
function groupRowsIntoProducts(
  rows: LightspeedRow[],
  outlets: string[],
  warnings: POSParseWarning[]
): POSParsedProduct[] {
  // Step 1: Group rows by product id to collect composite components
  const rowsById = new Map<string, LightspeedRow[]>();
  for (const row of rows) {
    if (!row.id) {
      warnings.push({
        row: row.rowNumber,
        message: `Row ${row.rowNumber}: Skipping row with no product ID`,
        severity: 'warning',
      });
      continue;
    }
    const existing = rowsById.get(row.id) || [];
    existing.push(row);
    rowsById.set(row.id, existing);
  }

  // Step 2: For each product id group, separate the primary row from composite rows
  interface ProductUnit {
    primaryRow: LightspeedRow;
    compositeRows: LightspeedRow[];
  }

  const productUnits: ProductUnit[] = [];

  for (const [, groupRows] of rowsById) {
    // The primary row is the one that is NOT a composite component row
    const primaryRow = groupRows.find(r => !isCompositeComponentRow(r));
    const compositeRows = groupRows.filter(r => isCompositeComponentRow(r));

    if (!primaryRow) {
      // All rows are composite components without a parent -- skip with warning
      warnings.push({
        row: groupRows[0].rowNumber,
        message: `Row ${groupRows[0].rowNumber}: Found composite components without a parent product (id: ${groupRows[0].id})`,
        severity: 'warning',
      });
      continue;
    }

    productUnits.push({ primaryRow, compositeRows });
  }

  // Step 3: Group product units by `handle` to detect variant families
  const familiesByHandle = new Map<string, ProductUnit[]>();
  const standaloneUnits: ProductUnit[] = [];

  for (const unit of productUnits) {
    const handle = unit.primaryRow.handle;
    if (!handle) {
      standaloneUnits.push(unit);
      continue;
    }
    const existing = familiesByHandle.get(handle) || [];
    existing.push(unit);
    familiesByHandle.set(handle, existing);
  }

  const products: POSParsedProduct[] = [];

  // Step 4: Process variant families (multiple units sharing same handle)
  for (const [, familyUnits] of familiesByHandle) {
    if (familyUnits.length === 1) {
      // Single product with this handle -- may or may not have variant options
      const unit = familyUnits[0];
      const product = buildProduct(unit, outlets, hasVariantOptions(unit.primaryRow));
      products.push(product);
    } else {
      // Multiple products share the same handle = variant family
      const product = buildVariantFamily(familyUnits, outlets);
      products.push(product);
    }
  }

  // Step 5: Process standalone products (no handle)
  for (const unit of standaloneUnits) {
    const product = buildProduct(unit, outlets, false);
    products.push(product);
  }

  return products;
}

/**
 * Build a single (non-variant-family) product from a product unit.
 */
function buildProduct(
  unit: { primaryRow: LightspeedRow; compositeRows: LightspeedRow[] },
  outlets: string[],
  isSingleVariant: boolean
): POSParsedProduct {
  const row = unit.primaryRow;
  const hasComposites = unit.compositeRows.length > 0;
  const hasVariants = isSingleVariant && hasVariantOptions(row);

  let productType: POSParsedProduct['product_type'] = 'item';
  if (hasComposites && hasVariants) {
    productType = 'composite_variant';
  } else if (hasComposites) {
    productType = 'composite';
  } else if (hasVariants) {
    productType = 'variant';
  }

  const product: POSParsedProduct = {
    sourceRows: [row.rowNumber, ...unit.compositeRows.map(r => r.rowNumber)],
    name: row.name,
    sku: row.sku,
    description: row.description || undefined,
    category: row.product_category || undefined,
    price: toNumber(row.retail_price) ?? 0,
    cost: toNumber(row.supply_price),
    // Lightspeed: active=1 -> Sellable, active=0 -> Raw Goods
    is_sellable: row.active === '1',
    track_inventory: row.track_inventory === '1',
    product_type: productType,
    supplier_name: row.supplier_name || undefined,
    supplier_code: row.supplier_code || undefined,
    outletInventory: extractOutletInventory(row, outlets),
  };

  // Add composite components
  if (hasComposites) {
    product.compositeComponents = unit.compositeRows.map(cr => ({
      name: cr.composite_name,
      sku: cr.composite_sku,
      quantity: toNumber(cr.composite_quantity) ?? 1,
    }));
  }

  // If this single product has variant options, create a one-variant array
  if (hasVariants) {
    const attrs = extractVariantAttributes(row);
    product.variants = [{
      name: buildVariantName(attrs),
      sku: row.sku || undefined,
      price: toNumber(row.retail_price) ?? 0,
      cost: toNumber(row.supply_price),
      attributes: attrs,
      outletInventory: extractOutletInventory(row, outlets),
    }];
  }

  return product;
}

/**
 * Build a variant family product from multiple product units that share the same handle.
 */
function buildVariantFamily(
  units: Array<{ primaryRow: LightspeedRow; compositeRows: LightspeedRow[] }>,
  outlets: string[]
): POSParsedProduct {
  // Use the first unit as the "parent" for shared product fields
  const parentRow = units[0].primaryRow;
  const hasAnyComposites = units.some(u => u.compositeRows.length > 0);

  const variants: POSParsedVariant[] = [];
  let allCompositeComponents: POSParsedCompositeComponent[] = [];
  const allSourceRows: number[] = [];

  for (const unit of units) {
    const row = unit.primaryRow;
    allSourceRows.push(row.rowNumber);
    allSourceRows.push(...unit.compositeRows.map(r => r.rowNumber));

    const attrs = extractVariantAttributes(row);
    variants.push({
      name: buildVariantName(attrs) || row.sku || row.name,
      sku: row.sku || undefined,
      price: toNumber(row.retail_price) ?? 0,
      cost: toNumber(row.supply_price),
      attributes: attrs,
      outletInventory: extractOutletInventory(row, outlets),
    });

    // Collect composite components (these may be variant-specific)
    if (unit.compositeRows.length > 0) {
      allCompositeComponents = allCompositeComponents.concat(
        unit.compositeRows.map(cr => ({
          name: cr.composite_name,
          sku: cr.composite_sku,
          quantity: toNumber(cr.composite_quantity) ?? 1,
        }))
      );
    }
  }

  const productType = hasAnyComposites ? 'composite_variant' : 'variant';

  const product: POSParsedProduct = {
    sourceRows: allSourceRows,
    name: parentRow.name,
    sku: parentRow.sku,
    description: parentRow.description || undefined,
    category: parentRow.product_category || undefined,
    price: toNumber(parentRow.retail_price) ?? 0,
    cost: toNumber(parentRow.supply_price),
    is_sellable: parentRow.active === '1',
    track_inventory: parentRow.track_inventory === '1',
    product_type: productType,
    supplier_name: parentRow.supplier_name || undefined,
    supplier_code: parentRow.supplier_code || undefined,
    variants,
    outletInventory: extractOutletInventory(parentRow, outlets),
  };

  if (allCompositeComponents.length > 0) {
    product.compositeComponents = allCompositeComponents;
  }

  return product;
}

/**
 * Read and parse a CSV file using PapaParse.
 */
function readCSV(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        resolve(results.data as string[][]);
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      },
    });
  });
}

// ---------- Public API: LightspeedParser ----------

export const LightspeedParser: POSSourceParser = {
  id: 'lightspeed',
  name: 'Lightspeed',
  description: 'Import products from a Lightspeed POS CSV export',
  acceptedFileTypes: ['.csv'],
  isAvailable: true,

  async detectOutlets(file: File): Promise<string[]> {
    const data = await readCSV(file);
    if (data.length < 1) return [];

    const headers = data[0].map(h => h.trim());
    const { outlets } = parseOutletColumns(headers);
    return outlets;
  },

  async parse(
    file: File,
    _outletMapping?: OutletLocationMapping
  ): Promise<POSImportData> {
    const data = await readCSV(file);

    if (data.length < 2) {
      return {
        products: [],
        categories: [],
        detectedOutlets: [],
        parseWarnings: [{
          message: 'CSV file is empty or contains only headers',
          severity: 'warning',
        }],
        summary: {
          totalProducts: 0,
          simpleProducts: 0,
          variantProducts: 0,
          compositeProducts: 0,
          compositeVariantProducts: 0,
          totalVariants: 0,
          totalComponents: 0,
        },
      };
    }

    const headers = data[0].map(h => h.trim());
    const columnMap = buildColumnIndexMap(headers);
    const { outlets, outletColumns } = parseOutletColumns(headers);
    const warnings: POSParseWarning[] = [];

    // Parse all data rows
    const parsedRows: LightspeedRow[] = [];
    for (let i = 1; i < data.length; i++) {
      const row = parseRow(data[i], i + 1, columnMap, outletColumns);
      parsedRows.push(row);
    }

    // Group rows into products
    const products = groupRowsIntoProducts(parsedRows, outlets, warnings);

    // Extract unique categories
    const categorySet = new Set<string>();
    for (const product of products) {
      if (product.category) {
        categorySet.add(product.category);
      }
    }

    // Build summary stats
    const simpleProducts = products.filter(p => p.product_type === 'item').length;
    const variantProducts = products.filter(p => p.product_type === 'variant').length;
    const compositeProducts = products.filter(p => p.product_type === 'composite').length;
    const compositeVariantProducts = products.filter(p => p.product_type === 'composite_variant').length;
    const totalVariants = products.reduce((sum, p) => sum + (p.variants?.length ?? 0), 0);
    const totalComponents = products.reduce((sum, p) => sum + (p.compositeComponents?.length ?? 0), 0);

    return {
      products,
      categories: Array.from(categorySet),
      detectedOutlets: outlets,
      parseWarnings: warnings,
      summary: {
        totalProducts: products.length,
        simpleProducts,
        variantProducts,
        compositeProducts,
        compositeVariantProducts,
        totalVariants,
        totalComponents,
      },
    };
  },
};
